import { t } from '@lingui/core/macro';

import type { AdjustmentVersion } from '@/react-query/useGetSharpenedImages';

import type { AdjustmentType } from './atoms';
export interface AllFiltersMap {
  label: string;
  value: AdjustmentVersion | null;
}
export interface DefaultFilterInfo {
  currentFilter: AdjustmentVersion | null;
  allFilters: AllFiltersMap[];
  showMultipleHdLabels?: boolean;
}

export const HD_SLOT_CONFIG = [
  { preferenceKey: 'adjustmentVersion' as const, label: 'HD1' },
  { preferenceKey: 'adjustmentVersionHD2' as const, label: 'HD2' },
  { preferenceKey: 'panoAdjustmentVersion' as const, label: 'Pano' },
];

export type PreferencesForDefaultFilter = {
  adjustmentVersion?: string;
  adjustmentVersionHD2?: string;
  panoAdjustmentVersion?: string;
  enableSharpeningHd2Selection?: boolean;
  enablePanoAdjustmentSelection?: boolean;
};

export function getDefaultFilterInfo(
  preferences: PreferencesForDefaultFilter | undefined,
  selectedAdjustmentType?: AdjustmentType | undefined,
): DefaultFilterInfo {
  const allFilters: AllFiltersMap[] = HD_SLOT_CONFIG.map(
    ({ preferenceKey, label }) => ({
      label,
      value: (preferences?.[preferenceKey] as AdjustmentVersion) ?? null,
    }),
  );

  const currentFilter =
    allFilters.find((filter) => filter.label === selectedAdjustmentType)
      ?.value ?? null;
  const showMultipleHdLabels =
    preferences?.enableSharpeningHd2Selection ||
    preferences?.enablePanoAdjustmentSelection;
  return { currentFilter, allFilters, showMultipleHdLabels };
}

/** Options for the filter select, including deprecated defaults not in the base list. */
export function getFilterOptions(
  baseOptions: AdjustmentVersion[],
  defaultFilterInfo: DefaultFilterInfo,
): (AdjustmentVersion | null)[] {
  const { currentFilter, allFilters, showMultipleHdLabels } = defaultFilterInfo;
  const defaultValues = showMultipleHdLabels
    ? allFilters
        .map((s) => s.value)
        .filter(
          (v): v is AdjustmentVersion => v != null && !baseOptions.includes(v),
        )
    : currentFilter && !baseOptions.includes(currentFilter)
      ? [currentFilter]
      : [];
  const unique = [...new Set(defaultValues)];
  return [null, ...unique, ...baseOptions];
}

export const adjustmentVersionLabel = (filter: AdjustmentVersion | null) => {
  const adjustmentVersionDescriptions: Record<
    AdjustmentVersion,
    { label: string; description: string }
  > = {
    '6.0.0.enhanced': {
      label: t`Crisp 1`,
      description: t`Boosts contrast and edge clarity for a sharper look.`,
    },
    '7.1.0.enhanced': {
      label: t`Smooth 1`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '7.2.0.enhanced': {
      label: t`Balanced 2`,
      description: t`Minimal enhancement that preserves tonal balance.`,
    },
    '7.3.0.enhanced': {
      label: t`Crisp 2`,
      description: t`Boosts contrast and edge clarity for a sharper look.`,
    },
    '7.4.0.enhanced': {
      label: t`Crisp 3`,
      description: t`Boosts contrast and edge clarity for a sharper look.`,
    },
    '7.4.1.enhanced': {
      label: t`Crisp 4`,
      description: t`Boosts contrast and edge clarity for a sharper look.`,
    },
    '7.5.0.enhanced': {
      label: t`Smooth 2`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '7.5.1.enhanced': {
      label: t`Smooth 2.1`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '7.5.2.enhanced': {
      label: t`Smooth 2.2`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '7.5.3.enhanced': {
      label: t`Smooth 3`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '7.6.0.enhanced': {
      label: t`Adapt Endo`,
      description: t`Sharpening similar to Carestream\'s \'Endo 3\' preset`,
    },
    '8.0.0.enhanced': {
      label: t`Smooth 4`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '8.0.1.enhanced': {
      label: t`Smooth 5`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
  };

  if (!filter) {
    return {
      label: t`Original`,
      description: t`Unprocessed baseline view with no enhancement applied.`,
    };
  }
  return (
    adjustmentVersionDescriptions[filter] || {
      label: filter,
      description: `Version ${filter}`,
    }
  );
};
